import alertService from '@jetbrains/ring-ui-built/components/alert-service/alert-service'
import {skipToken} from '@reduxjs/toolkit/query'
import deepEqual from 'fast-deep-equal'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import {createElement, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {shallowEqual} from 'react-redux'
import {fetchQuery, graphql, useFragment, useMutation, useRelayEnvironment} from 'react-relay'
import {useLocation, useSearchParams} from 'react-router'

import {useAppDispatch, useAppSelector, useAppStore} from '../../../../hooks/react-redux'
import {useRefetchableLazyLoadQuery} from '../../../../hooks/relay/useRefetchableLazyLoadQuery'
import {useBooleanQueryParam} from '../../../../hooks/routes'
import {useSyncEffect} from '../../../../hooks/useSyncEffect'
import type {State} from '../../../../reducers/types'
import {mergeIfDifferentAndPrune} from '../../../../reducers/utils'
import {getBranchLocator} from '../../../../rest/locators'
import {type Build, restApi} from '../../../../services/rest'
import type {PipelineId, ProjectId} from '../../../../types'
import {defaultBranch, DEFAULT_NAME as DEFAULT_BRANCH_NAME} from '../../../../utils/branchNames'
import {emptyArray as frozenEmptyArray, getEmptyHash} from '../../../../utils/empty'
import {getExtractErrorMessage} from '../../../../utils/extractError'
import {generateFreeName} from '../../../../utils/generateFreeName'
import {subscribeOnBuildTypeEvents, subscribeOnProjectEvents} from '../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_ADDED_TO_QUEUE,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../utils/subscriptionEvents'
import type {ConfigurationStorageCanEditFragment$key} from '../components/ConfigurationStorage/__generated__/ConfigurationStorageCanEditFragment.graphql'
import {useCanEditVersionedSettings} from '../components/ConfigurationStorage/ConfigurationStorage.hooks'
import type {pipelineHeadBuildTypeFragment$key} from '../hooks/__generated__/pipelineHeadBuildTypeFragment.graphql'
import {
  useJobIdState,
  usePipelineHeadId,
  usePipelineId,
  usePipelineInternalId,
  usePipelineScope,
} from '../hooks/pipeline'
import {useVersionedSettingsStatus} from '../hooks/useVersionedSettings'
import {usePipelineDebugArg} from '../PipelineRunPage/PipelineRunPage.hooks'
import {pipelinesApi} from '../services/pipelinesApi'
import type {
  CommitDetails,
  NewPipeline,
  NewVcsRootDto,
  Pipeline,
  VersionedSettingsDto,
} from '../services/pipelinesApi.types'
import type {DependencyGroup, Job, PipelineDependencyDetails, PipelineDependencyId} from '../types'
import type {GraphNode} from '../utils/graph'
import {createGraph} from '../utils/graph'

import type {EditPipelinePageApplySecretQuery} from './__generated__/EditPipelinePageApplySecretQuery.graphql'
import type {EditPipelinePageCancelBuildMutation} from './__generated__/EditPipelinePageCancelBuildMutation.graphql'
import type {EditPipelinePageDependencyFragment$key} from './__generated__/EditPipelinePageDependencyFragment.graphql'
import type {EditPipelinePageLastRunQuery} from './__generated__/EditPipelinePageLastRunQuery.graphql'
import {commitPipelineDraft, discardPipelineDraft} from './EditPipelinePage.actions'
import {applySecretQuery} from './EditPipelinePage.queries'
import {
  getDebug,
  getDeleted,
  getDraft,
  getDraftJobs,
  getIsDebugRunning,
  getIsPipelineValid,
  getJobName,
  getNewSettings,
  getNewYaml,
  getOriginal,
  getPipeline,
  getPipelineErrorMessage,
  getResolvedSettings,
  getStringifiedDependencies,
  getYaml,
  getYamlDraft,
  getYamlValidity,
  getHasEffectiveChangesFromOriginal,
  isDraftChanged,
  isDraftSettingsChanged,
} from './EditPipelinePage.selectors'
import PipelineAlertServiceError from './PipelineAlertServiceError/PipelineWrapperAlertMessage'
import {
  debug,
  pipelineDraft,
  pipelineDraftForm,
  pipelineSecrets,
  pipelineStepSecrets,
} from './slices/EditPipelinePage.slices'
import {FormType} from './slices/EditPipelinePage.slices.types'
import {getStepSecretKey} from './slices/EditPipelinePage.slices.utils'

export const useJobId = (): string | null => {
  const [searchParams] = useSearchParams()
  return searchParams.get('job')
}

export function usePipelineData(id: PipelineId, branch: string) {
  const isPipelineLoadedRef = useRef(false)
  // Tracks the pipeline whose data was last loaded, so we can tell a branch switch (same pipeline, only the branch
  // query param changed) from a pipeline switch (a different pipeline id via the sidebar / route).
  const loadedPipelineIdRef = useRef<PipelineId | null>(null)
  const pipelineData = pipelinesApi.endpoints.getPipelineById.useQueryState(
    {id, branch},
    {
      selectFromResult: ({currentData, error}) => {
        const isPipelineLoaded = isPipelineLoadedRef.current
        const hasCurrentData = currentData != null
        const isLoading = !hasCurrentData && error == null
        const isSwitching = isPipelineLoaded && isLoading

        return {
          pipeline: currentData,
          yaml: currentData?.yaml,
          hasCurrentData,
          isPipelineSwitching: isSwitching,
          // A branch switch reloads within the same mounted pipeline; the editor must be hidden while the new
          // branch loads, otherwise the previous branch's content flickers before the empty state appears.
          isBranchSwitching: isSwitching && loadedPipelineIdRef.current === id,
        }
      },
    },
  )

  useEffect(() => {
    if (pipelineData.hasCurrentData) {
      isPipelineLoadedRef.current = true
      loadedPipelineIdRef.current = id
    }
  }, [pipelineData.hasCurrentData, id])

  return pipelineData
}

export const usePipelineNameOrDraft = () => {
  const {pipelineId: id, branch} = usePipelineScope()
  return useAppSelector(state => getDraft(state, id, branch)?.name)
}

export const usePipelineIntegrationsOrDraft = () => {
  const {pipelineId: id, branch} = usePipelineScope()
  return useAppSelector(state => getDraft(state, id, branch)?.integrations)
}

export const usePipelineAdditionalVcsRootsOrDraft = () => {
  const {pipelineId: id, branch} = usePipelineScope()
  return (
    useAppSelector(state => getDraft(state, id, branch)?.additionalVcsRoots) ??
    (frozenEmptyArray as NewVcsRootDto[])
  )
}

const useExistingJobs = () => {
  const {pipelineId: id, branch} = usePipelineScope()
  const {existing} = pipelinesApi.endpoints.getPipelineById.useQuery(
    {id, branch},
    {
      selectFromResult: ({data}) => ({existing: data?.settings.jobs}),
    },
  )
  return existing
}

const emptyArray: never[] = []
export const usePipelineJobIdsOrDraft = (): ReadonlyArray<string> => {
  const {pipelineId: id, branch} = usePipelineScope()
  return useAppSelector(state => Object.keys(getDraftJobs(state, id, branch) ?? {}), shallowEqual)
}

const vcsRootConflictPatterns = [
  /VCS root with (?:ID|id) ["']?([A-Za-z0-9_.-]+)["']?(?: already exists)?/gi,
]

const getConflictingVcsRootIds = (message?: string | null) => {
  if (!message) {
    return []
  }
  const ids = new Set<string>()
  vcsRootConflictPatterns.forEach(pattern => {
    const matches = message.matchAll(pattern)
    for (const match of matches) {
      if (match[1]) {
        ids.add(match[1])
      }
    }
  })
  return Array.from(ids)
}

const getPipelineSaveErrorMessage = (message: string) => {
  if (getConflictingVcsRootIds(message).length > 0) {
    return `Cannot save pipeline because a repository ID already exists. Please delete the repository and add it again.\n\n${message}`
  }
  return message
}

export const showPipelineError = (message: string) => {
  alertService.error(
    createElement(PipelineAlertServiceError, null, getPipelineSaveErrorMessage(message)),
  )
}

export const useNonDeletedPipelineJobIdsOrDraft = (): ReadonlyArray<string> => {
  const jobIds = usePipelineJobIdsOrDraft()
  const {pipelineId: id, branch} = usePipelineScope()
  const deleted = useAppSelector(state => getDeleted(state, id, branch)?.jobs)

  return useMemo(() => jobIds.filter(jobId => !deleted?.includes(jobId)), [jobIds, deleted])
}

export const usePipelineExistingJobIdsSet = (): Set<string> => {
  const existing = useExistingJobs()
  return useMemo(() => new Set(existing != null ? Object.keys(existing) : emptyArray), [existing])
}

const usePipelineExistingJob = (jobId: string): Job | undefined => {
  const existingJobs = useExistingJobs()

  return existingJobs?.[jobId]
}

export const usePipelineDraftExistingJob = (jobId: string): Job | undefined => {
  const {pipelineId, branch} = usePipelineScope()
  const draftExistingJobs = useAppSelector(state => getDraftJobs(state, pipelineId, branch))

  return draftExistingJobs?.[jobId]
}

export const useDoesJobExist = (jobId: string) => {
  const existing = usePipelineExistingJob(jobId)
  return existing != null
}

export const usePipelineGraph = (stringifiedDependencies: string): Record<string, GraphNode> =>
  useMemo(() => {
    const dependencies = JSON.parse(stringifiedDependencies)
    return createGraph({
      data: Object.keys(dependencies),
      getId: id => id,
      getDependencies: id => dependencies[id],
    })
  }, [stringifiedDependencies])

export const usePipelineDraftGraph = (): Record<string, GraphNode> => {
  const {pipelineId: id, branch} = usePipelineScope()
  const stringifiedDependencies = useAppSelector(state =>
    getStringifiedDependencies(getDraftJobs(state, id, branch)),
  )
  return usePipelineGraph(stringifiedDependencies)
}

export const useGenerateFreeJobName = () => {
  const {pipelineId, branch} = usePipelineScope()
  const usedNames = useAppSelector(
    state =>
      Object.entries(getDraftJobs(state, pipelineId, branch) ?? {}).flatMap(([jobId, job]) => [
        job.name ?? jobId,
        getJobName(state, pipelineId, branch, jobId),
      ]),
    shallowEqual,
  )

  return useCallback(
    (): string =>
      generateFreeName({
        getName: iteration => `Job ${iteration + 1}`,
        usedNames,
        errorMessage: 'Error while generating new job name',
      }),
    [usedNames],
  )
}

export const useGenerateFreeJobId = () => {
  const {pipelineId, branch} = usePipelineScope()
  const usedIds = useAppSelector(
    state => Object.keys(getDraftJobs(state, pipelineId, branch) ?? {}),
    shallowEqual,
  )

  const [generateId] = restApi.endpoints.generateId.useMutation()

  return useCallback(
    async (name: string): Promise<string> => {
      const generatedId = await generateId({
        object: 'buildType',
        name,
      }).unwrap()
      return generateFreeName({
        getName: iteration => (iteration === 0 ? generatedId : `${generatedId}_${iteration + 1}`),
        usedNames: usedIds,
        errorMessage: 'Error while generating new job id',
      })
    },
    [generateId, usedIds],
  )
}

export const useGenerateCloneJobNames = ({name}: {name: string}) => {
  const {pipelineId, branch} = usePipelineScope()
  const generateFreeJobId = useGenerateFreeJobId()
  const usedNames = useAppSelector(
    state =>
      Object.entries(getDraftJobs(state, pipelineId, branch) ?? {}).flatMap(([jobId, job]) => [
        job.name ?? jobId,
        getJobName(state, pipelineId, branch, jobId),
      ]),
    shallowEqual,
  )

  return useCallback(async () => {
    const newJobName = generateFreeName({
      getName: iteration => `${name} clone${iteration > 0 ? ` (${iteration})` : ''}`,
      usedNames,
      errorMessage: 'Error while generating cloned job name',
    })

    const newId = await generateFreeJobId(newJobName)

    return {name: newJobName, id: newId}
  }, [generateFreeJobId, name, usedNames])
}

export const useIsJobDeleted = (jobId: string) => {
  const {pipelineId: id, branch} = usePipelineScope()
  return useAppSelector(state => getDeleted(state, id, branch)?.jobs?.includes(jobId) ?? false)
}

function useLastRunLocator(pipelineHeadBuildTypeKey: pipelineHeadBuildTypeFragment$key | null) {
  const headId = usePipelineHeadId(pipelineHeadBuildTypeKey)
  const baseLocator = [
    `count:1,buildType:(id:${headId}),state:finished,personal:false`,
    getBranchLocator(defaultBranch, true),
  ]
    .filter(Boolean)
    .join(',')
  return `item(defaultFilter:false,${baseLocator}),item(${baseLocator})`
}

const dependencyFragment = graphql`
  fragment EditPipelinePageDependencyFragment on Build {
    snapshotDependencies {
      build {
        ...EditPipelineJobLastRunFragment
        ...useJobStepRunnerSummarizeErrorPreviewRunFragment
        ...JobDirectoryPopupLastRunFragment
        buildType {
          name
        }
        canceledInfo {
          timestamp
        }
      }
    }
  }
`
const useDependency = (buildKey: EditPipelinePageDependencyFragment$key | null, dep: string) => {
  const build = useFragment(dependencyFragment, buildKey)
  return build?.snapshotDependencies?.build?.find(item => item.buildType?.name === dep) ?? null
}

const lastRunQuery = graphql`
  query EditPipelinePageLastRunQuery($locator: String!) {
    builds(locator: $locator) {
      build {
        ...EditPipelinePageDependencyFragment
      }
    }
  }
`
export function useLastRun(
  id: string,
  pipelineHeadBuildTypeKey: pipelineHeadBuildTypeFragment$key | null,
) {
  const locator = useLastRunLocator(pipelineHeadBuildTypeKey)
  const [{builds}, refetch] = useRefetchableLazyLoadQuery<EditPipelinePageLastRunQuery>(
    lastRunQuery,
    {locator},
  )
  const {pipelineId, branch} = usePipelineScope()
  const name = useAppSelector(state => getJobName(state, pipelineId, branch, id))
  const lastRun = useDependency(builds?.build?.[0] ?? null, name)
  const lastFinishedRun = useDependency(builds?.build?.[1] ?? null, name)
  const pipelineInternalId = usePipelineInternalId(pipelineId)

  useEffect(
    () =>
      pipelineInternalId != null
        ? subscribeOnProjectEvents(
            pipelineInternalId,
            [
              BUILD_TYPE_ADDED_TO_QUEUE,
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
            ],
            refetch,
          )
        : undefined,
    [pipelineInternalId, refetch],
  )

  if (lastRun == null || lastRun.canceledInfo != null) {
    return lastFinishedRun
  }
  return lastRun
}
export const useIsSettingsChanged = () => {
  const {pipelineId, branch} = usePipelineScope()
  const hasChangesDraft = useAppSelector(state =>
    isDraftSettingsChanged(getPipeline(state, pipelineId, branch)),
  )
  const hasChangesOriginal = useAppSelector(state => {
    const {yaml: updatedYaml, ...updated}: Partial<NewPipeline> =
      getResolvedSettings(state, pipelineId, branch) ?? {}
    const {jobs, ...restUpdatedSettings} = updated?.settings ?? {}
    const updatedPipeline = {...updated, settings: restUpdatedSettings}
    const {yaml: originalYaml, ...original}: Partial<Pipeline> =
      getOriginal(state, pipelineId, branch) ?? {}
    const {jobs: originalJobs, ...restOriginalSettings} = original?.settings ?? {}
    const originalPipeline = {...original, settings: restOriginalSettings}
    return (
      (!isEmpty(updated) && !deepEqual(updatedPipeline, originalPipeline)) ||
      !isEmpty(state.pipelines.pipelineSecrets[pipelineId]?.[branch]) ||
      !isEmpty(state.pipelines.pipelineStepSecrets[pipelineId]?.[branch])
    )
  })
  return hasChangesDraft || hasChangesOriginal
}

export function useHasVisualChanges() {
  const {pipelineId, branch} = usePipelineScope()

  const hasChangesDraft = useAppSelector(state =>
    isDraftChanged(getPipeline(state, pipelineId, branch)),
  )
  const hasChangesOriginal = useAppSelector(state =>
    getHasEffectiveChangesFromOriginal(state, pipelineId, branch),
  )

  return hasChangesDraft || hasChangesOriginal
}

export function useSavePipeline() {
  const [jobId, setJobId] = useJobIdState()
  const {pipelineId, branch} = usePipelineScope()
  const isYaml = useBooleanQueryParam('yaml')

  const dispatch = useAppDispatch()
  const {getState} = useAppStore()

  const [updatePipeline, {isLoading, error, reset}] =
    pipelinesApi.endpoints.updatePipeline.useMutation({
      fixedCacheKey: 'shared-save-pipeline',
    })

  const handleSaveSuccess = (data: Pipeline, commitDetails?: CommitDetails) => {
    const targetBranch = commitDetails?.branch ?? DEFAULT_BRANCH_NAME
    if (jobId != null && data.settings.jobs?.[jobId] == null) {
      setJobId(null)
    }
    dispatch(commitPipelineDraft(pipelineId, targetBranch, data))
    // when settings are saved to another branch, drop the stale draft left on the current one
    if (targetBranch !== branch) {
      dispatch(discardPipelineDraft(pipelineId, branch))
    }
  }

  const saveYamlChanges = async (commitDetails?: CommitDetails) => {
    const state = getState()
    const body = getSaveYAMLChangesBody(state, pipelineId, branch)

    if (body != null) {
      dispatch(pipelineDraftForm.actions.submit())
      const result = await updatePipeline({
        id: pipelineId,
        pipeline: body,
        commitDetails,
      })
      if ('data' in result && result.data != null) {
        handleSaveSuccess(result.data, commitDetails)
        return true
      }
    }

    return false
  }

  const saveVisualChanges = async (commitDetails?: CommitDetails) => {
    const state = getState()
    const updated = getResolvedSettings(state, pipelineId, branch)

    const hasChangesOriginal = getHasEffectiveChangesFromOriginal(state, pipelineId, branch)

    if (hasChangesOriginal && updated != null) {
      dispatch(pipelineDraftForm.actions.submit())
      const result = await updatePipeline({
        id: pipelineId,
        pipeline: updated,
        commitDetails,
      })
      if ('data' in result && result.data != null) {
        handleSaveSuccess(result.data, commitDetails)
        return true
      }
    }

    return false
  }

  return {
    savePipeline: isYaml ? saveYamlChanges : saveVisualChanges,
    isLoading,
    error,
    reset,
  }
}

export const usePipelineServerValidation = (isYaml: boolean) => {
  const {pipelineId, branch} = usePipelineScope()
  const {getState} = useAppStore()
  const [validatePipeline, {isLoading: isValidating}] =
    pipelinesApi.endpoints.validatePipeline.useMutation()

  const validate = useCallback(async () => {
    const state = getState()
    const body = isYaml
      ? getSaveYAMLChangesBody(state, pipelineId, branch)
      : getResolvedSettings(state, pipelineId, branch)
    if (body == null) {
      return true
    }
    const result = await validatePipeline({pipelineId, body})
    if ('error' in result && result.error != null) {
      showPipelineError(getExtractErrorMessage(result.error))
      return false
    }
    return true
  }, [branch, getState, isYaml, pipelineId, validatePipeline])

  return {isValidating, validate}
}

const ALERT_TIMEOUT = 2000

export function useApplyYAMLChangesHandler() {
  const {pipelineId, branch} = usePipelineScope()
  const dispatch = useAppDispatch()
  const {getState} = useAppStore()

  return () => {
    const state = getState()
    const newSettings = getNewSettings(state, pipelineId, branch)
    const pipelineDraftState = getDraft(state, pipelineId, branch)

    if (pipelineDraftState !== undefined) {
      const draft = {
        ...pipelineDraftState,
        settings: mergeIfDifferentAndPrune(pipelineDraftState.settings, newSettings),
      }
      dispatch(pipelineSecrets.actions.unsetAll({pipelineId, branch}))
      dispatch(pipelineStepSecrets.actions.unsetAll({pipelineId, branch}))
      dispatch(
        hasYAMLDraft(state, pipelineId, branch)
          ? pipelineDraft.actions.setDraftPreservingCollision({id: pipelineId, draft, branch})
          : pipelineDraft.actions.updateDraft({id: pipelineId, draft, branch}),
      )
    }
  }
}

export function useIsEditPipelineYAMLValid() {
  const {pipelineId, branch} = usePipelineScope()
  return useAppSelector(state => getIsPipelineValid(state, {pipelineId, branch}))
}

function hasYAMLDraft(state: State, pipelineId: PipelineId, branch: string): boolean {
  return getYamlDraft(state, pipelineId, branch)?.yaml != null
}

export function getSaveYAMLChangesBody(
  state: State,
  pipelineId: PipelineId,
  branch: string,
): NewPipeline | null {
  if (hasYAMLDraft(state, pipelineId, branch)) {
    const yaml = getNewYaml(state, pipelineId, branch)
    const newSettings = getNewSettings(state, pipelineId, branch)
    const pipelineDraftState = getDraft(state, pipelineId, branch)

    return pipelineDraftState == null
      ? null
      : {
          ...pipelineDraftState,
          settings: mergeIfDifferentAndPrune(pipelineDraftState.settings, newSettings),
          yaml,
        }
  }

  const updated = getResolvedSettings(state, pipelineId, branch)

  return updated == null
    ? null
    : {
        ...updated,
        yaml: getNewYaml(state, pipelineId, branch),
      }
}

export function useYAMLHasChanges() {
  const {pipelineId, branch} = usePipelineScope()
  return useAppSelector(state => {
    const serverYaml =
      pipelinesApi.endpoints.getPipelineById
        .select({id: pipelineId, branch})(state as never)
        .data?.yaml?.trim() ?? ''
    return serverYaml !== getNewYaml(state, pipelineId, branch)?.trim()
  })
}

export const useDraftStepError = (props: {stepIndex: number; name?: string}) => {
  const {stepIndex, name} = props
  const jobId = useJobId()!
  const {pipelineId, branch} = usePipelineScope()
  return useAppSelector(
    getPipelineErrorMessage({
      pipelineId,
      branch,
      jobId,
      formType: FormType.STEP,
      formId: stepIndex,
      fieldName: name,
    }),
  )
}

export const useHasNonDeletedDependants = (jobId: string) => {
  const {pipelineId, branch} = usePipelineScope()

  const graph = usePipelineDraftGraph()
  const deleted = useAppSelector(state => getDeleted(state, pipelineId, branch)?.jobs)
  return (
    graph[jobId] != null &&
    Array.from(graph[jobId].successors).some(successorId => !deleted?.includes(successorId))
  )
}

export const useConfigurationStorage = (
  pipelineKey: ConfigurationStorageCanEditFragment$key | null,
) => {
  const canEditVersionedSettings = useCanEditVersionedSettings(pipelineKey)
  const {pipelineId: id, branch} = usePipelineScope()
  const dispatch = useAppDispatch()
  const [jobId, setJobId] = useJobIdState()

  const location = useLocation()
  const isCreated = location?.state?.isCreated ?? false

  const pipelineDraftState = useAppSelector(state => getDraft(state, id, branch))
  const vcsRoot = pipelineDraftState?.vcsRoot

  const [dialogVisible, setDialogVisible] = useState(false)

  const [checkVersionedSettings, {hasFileInRepo}] =
    pipelinesApi.endpoints.checkVersionedSettings.useLazyQuery({
      selectFromResult: ({data}) => ({hasFileInRepo: data ?? false}),
    })

  useVersionedSettingsStatus()
  const [updatePipeline, {isLoading, error}] = pipelinesApi.endpoints.updatePipeline.useMutation()

  const onConfigurationStorageChange = useCallback(
    async (settings: VersionedSettingsDto) => {
      if (pipelineDraftState) {
        dispatch(pipelineDraft.actions.setVersionedSettings({id, branch, storedInRepo: true}))
        dispatch(pipelineDraftForm.actions.submit())
        const result = await updatePipeline({
          id,
          pipeline: {
            ...pipelineDraftState,
            versionedSettings: settings,
          },
        })
        if ('data' in result && result.data != null) {
          if (jobId != null && result.data.settings.jobs?.[jobId] == null) {
            setJobId(null)
          }
          dispatch(pipelineDraft.actions.set({pipeline: result.data, branch}))
        }
        setDialogVisible(false)
        history.replaceState({}, '')
      }
    },
    [dispatch, id, branch, jobId, pipelineDraftState, setJobId, updatePipeline],
  )

  useEffect(() => {
    if (error != null) {
      const errorMessage = getExtractErrorMessage(error)
      alertService.error(createElement(PipelineAlertServiceError, null, errorMessage))
    }
  }, [error])

  useEffect(() => {
    if (isCreated && vcsRoot?.url) {
      const vcsRootId = vcsRoot.id
      if (vcsRootId) {
        checkVersionedSettings({vcsRootId})
      }
    }
  }, [checkVersionedSettings, isCreated, vcsRoot])

  useSyncEffect(() => {
    if (isCreated && canEditVersionedSettings) {
      setDialogVisible(hasFileInRepo)
    }
  }, [canEditVersionedSettings, hasFileInRepo, isCreated])

  return {
    dialogVisible,
    isLoading,
    onConfigurationStorageChange,
  }
}

function useFetchSecureToken() {
  const pipelineId = usePipelineId()
  const pipelineInternalId = usePipelineInternalId(pipelineId)
  const environment = useRelayEnvironment()
  return useCallback(
    async (input: string): Promise<string | null> => {
      const {addSecureToken} =
        (await fetchQuery<EditPipelinePageApplySecretQuery>(
          environment,
          applySecretQuery,
          {projectLocator: `internalId:${pipelineInternalId}`, input},
          {fetchPolicy: 'store-or-network'},
        ).toPromise()) ?? {}
      return addSecureToken ?? null
    },
    [environment, pipelineInternalId],
  )
}

export function useApplySecrets() {
  const {pipelineId, branch} = usePipelineScope()
  const dispatch = useAppDispatch()
  const fetchSecureToken = useFetchSecureToken()
  const secrets = useAppSelector(
    state => state.pipelines.pipelineSecrets[pipelineId]?.[branch] ?? getEmptyHash(),
  )
  // needed to avoid memoization reset on secret change
  const secretsRef = useRef<Record<string, string>>({})
  useEffect(() => {
    secretsRef.current = secrets
  }, [secrets])
  return useCallback(
    (filter: (savedName: string) => boolean = () => true) =>
      Promise.all(
        Object.entries(secretsRef.current)
          .filter(([savedName]) => filter(savedName))
          .map(async ([savedName, value]) => {
            if (!value) {
              dispatch(
                pipelineDraft.actions.updateSecret({id: pipelineId, branch, savedName, value}),
              )
              return
            }
            const token = await fetchSecureToken(value)
            if (token == null) {
              return
            }
            dispatch(
              pipelineDraft.actions.updateSecret({id: pipelineId, branch, savedName, value: token}),
            )
          }),
      ),
    [dispatch, fetchSecureToken, pipelineId, branch],
  )
}

export function useApplyStepSecrets() {
  const {pipelineId, branch} = usePipelineScope()
  const dispatch = useAppDispatch()
  const fetchSecureToken = useFetchSecureToken()
  const stepSecrets = useAppSelector(
    state => state.pipelines.pipelineStepSecrets[pipelineId]?.[branch] ?? getEmptyHash(),
  )
  // needed to avoid memoization reset on step secret change
  const stepSecretsRef = useRef(stepSecrets)
  useEffect(() => {
    stepSecretsRef.current = stepSecrets
  }, [stepSecrets])
  return useCallback(
    (filter: (key: string) => boolean = () => true) =>
      Promise.all(
        Object.entries(stepSecretsRef.current)
          .filter(([key]) => filter(key))
          .map(async ([, {jobId, index, name, value}]) => {
            const token = value ? await fetchSecureToken(value) : null
            if (value && token == null) {
              return
            }
            dispatch(
              pipelineDraft.actions.setStepParameter({
                id: pipelineId,
                branch,
                jobId,
                index,
                name,
                value: token,
              }),
            )
          }),
      ),
    [dispatch, fetchSecureToken, pipelineId, branch],
  )
}

export function useJobStepSecretControl({
  jobId,
  stepIndex,
  name,
}: {
  jobId: string
  stepIndex: number
  name: string
}) {
  const {pipelineId, branch} = usePipelineScope()
  const dispatch = useAppDispatch()
  const key = getStepSecretKey(jobId, stepIndex, name)
  const value = useAppSelector(
    state => state.pipelines.pipelineStepSecrets[pipelineId]?.[branch]?.[key]?.value,
  )
  const applyStepSecrets = useApplyStepSecrets()
  const savedValue = useAppSelector(state => {
    const step = getDraftJobs(state, pipelineId, branch)?.[jobId]?.steps?.[stepIndex]
    const saved = step ? get(step, name) : undefined
    return typeof saved === 'string' ? saved : undefined
  })
  const hasSavedToken = savedValue != null && savedValue.length > 0
  const changeHandler = useCallback(
    (newValue: string) => {
      dispatch(
        pipelineStepSecrets.actions.set({
          pipelineId,
          branch,
          key,
          jobId,
          index: stepIndex,
          name,
          value: newValue,
        }),
      )
    },
    [dispatch, branch, jobId, key, name, pipelineId, stepIndex],
  )
  const blurHandler = useCallback(
    () => applyStepSecrets(savedKey => savedKey === key),
    [applyStepSecrets, key],
  )
  return {value, hasSavedToken, changeHandler, blurHandler}
}

function usePredecessors(jobId?: string | null) {
  const graph = usePipelineDraftGraph()
  if (!jobId) {
    return new Set()
  }

  const {predecessors} = graph[jobId] ?? {
    predecessors: new Set([jobId]),
  }
  return new Set([...predecessors, jobId])
}

export function useIsDebugRunning() {
  const {pipelineId, branch} = usePipelineScope()
  const runId = useAppSelector(state => getDebug(state, pipelineId, branch)?.runId)
  const isRunning = useAppSelector(state => getIsDebugRunning(state, pipelineId, branch) ?? false)
  return !!runId && isRunning
}

export const useDebugJobIdMap = () => {
  const arg = usePipelineDebugArg()
  const {pipelineId, branch} = usePipelineScope()
  const pipelineRunId = useAppSelector(state => getDebug(state, pipelineId, branch)?.runId)
  const debugJobId = useAppSelector(state => getDebug(state, pipelineId, branch)?.jobId)
  const predecessors = usePredecessors(debugJobId)
  const {jobs, headInternalId, state, refetch} = restApi.endpoints.getBuildNormalized.useQuery(
    pipelineRunId ? arg : skipToken,
    {
      selectFromResult: ({data}) => {
        const run = data?.entities?.builds?.[data?.result!]
        const headId = run?.buildType
        const state = run?.state
        const dependencies = run?.['snapshot-dependencies']?.build
        return {
          jobs: dependencies ?? (emptyArray as Build[]),
          headInternalId: data?.entities?.buildTypes?.[headId!]?.internalId,
          state,
        }
      },
    },
  )

  const [firstJob] = jobs
  const childInternalId = firstJob?.buildType?.project?.internalId
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (pipelineRunId && state === 'finished') {
      dispatch(debug.actions.finish({pipelineId, branch}))
    }
  }, [branch, dispatch, pipelineId, pipelineRunId, state])

  useEffect(
    () =>
      childInternalId && pipelineRunId
        ? subscribeOnProjectEvents(
            childInternalId,
            [BUILD_TYPE_REMOVED_FROM_QUEUE, BUILD_STARTED, BUILD_FINISHED, BUILD_INTERRUPTED],
            refetch,
          )
        : undefined,
    [childInternalId, refetch, pipelineRunId],
  )

  useEffect(
    () =>
      headInternalId && pipelineRunId
        ? subscribeOnBuildTypeEvents(
            headInternalId,
            [BUILD_TYPE_REMOVED_FROM_QUEUE, BUILD_STARTED, BUILD_FINISHED, BUILD_INTERRUPTED],
            refetch,
          )
        : undefined,
    [headInternalId, refetch, pipelineRunId],
  )

  return useMemo(
    () =>
      jobs.reduce((acc, {id, attributes}) => {
        const jobIdValue = attributes?.entry?.find(
          entry => entry?.name === 'teamcity.pipeline.job.id',
        )?.value

        if (!jobIdValue || !id || !predecessors.has(jobIdValue)) {
          return acc
        }

        acc.set(jobIdValue, id)

        return acc
      }, new Map<string, number>()),
    [jobs, predecessors],
  )
}

const cancelBuildMutation = graphql`
  mutation EditPipelinePageCancelBuildMutation($buildLocator: String!) {
    cancelBuild(buildLocator: $buildLocator, input: {}) {
      id
    }
  }
`

export const getDebugArgs = (pipelineId: PipelineId, branch: string, id: string, yaml: string) => ({
  pipelineId,
  body: {
    branch,
    debug: {
      [id]: {breakpoints: []},
    },
    yaml,
  },
  fields: 'build(id,state,buildTypeInternalId),jobs(job(build(id,state,buildType(name))))',
})

export const useStopDebugOnYamlChange = () => {
  const {pipelineId, branch} = usePipelineScope()
  const dispatch = useAppDispatch()
  const yaml = useAppSelector(state => getYaml(state, pipelineId, branch))
  const debugYaml = useAppSelector(state => getDebug(state, pipelineId, branch)?.yaml)
  const isYamlKnown = useAppSelector(
    state => getYamlValidity(state, pipelineId, branch)?.isParsed != null,
  )

  useEffect(() => {
    if (isYamlKnown && debugYaml && yaml !== debugYaml) {
      dispatch(debug.actions.stop({pipelineId, branch}))
      alertService.message('The debug session has been stopped', ALERT_TIMEOUT)
    }
  }, [branch, debugYaml, dispatch, isYamlKnown, pipelineId, yaml])
}

export const useDebugJobActions = (id?: string) => {
  const {pipelineId, branch} = usePipelineScope()
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const debugRunId = useAppSelector(state => getDebug(state, pipelineId, branch)?.runId)

  const [runDebug] = pipelinesApi.endpoints.runPipelineDebug.useLazyQuery()
  const [cancelBuild] = useMutation<EditPipelinePageCancelBuildMutation>(cancelBuildMutation)

  const onDebug = useCallback(() => {
    if (!id) {
      return
    }
    const yaml = getYaml(store.getState(), pipelineId, branch)
    searchParams.delete('step')
    const searchParamsObject = Object.fromEntries(searchParams.entries())
    setSearchParams(searchParamsObject)

    dispatch(debug.actions.start({pipelineId, branch, jobId: id}))
    runDebug(getDebugArgs(pipelineId, branch, id, yaml))
  }, [branch, dispatch, id, pipelineId, runDebug, searchParams, setSearchParams, store])

  const onStopDebug = useCallback(() => {
    if (!debugRunId) {
      return
    }
    dispatch(debug.actions.stop({pipelineId, branch}))
    cancelBuild({variables: {buildLocator: `id:${debugRunId}`}})
  }, [branch, cancelBuild, debugRunId, dispatch, pipelineId])

  return {
    onDebug,
    onStopDebug,
  }
}

export const useGroupedDependencies = (
  dependencyIds?: PipelineDependencyId[],
  dependencyDetails?: Record<PipelineDependencyId, PipelineDependencyDetails>,
) => {
  const pipelineId = usePipelineId()
  const groupsMap = new Map<ProjectId, DependencyGroup>()

  dependencyIds?.forEach(dependencyId => {
    const details = dependencyDetails?.[dependencyId]
    const {projectId: id, parentProjectId, isPipeline} = details ?? {}
    if (dependencyId === pipelineId) {
      return
    }

    const unknownProjectId = '_unknown_project_id_' as ProjectId
    const projectId = id ?? unknownProjectId

    const group = groupsMap.get(projectId) ?? {
      projectId,
      parentProjectId,
      items: [],
      isUnknown: projectId === unknownProjectId,
    }

    group.items.push({
      id: dependencyId,
      name: details?.name ?? dependencyId,
      isPipeline,
      build: details?.build,
    })
    groupsMap.set(projectId, group)
  })
  return Array.from(groupsMap.values())
}
